import type { ApiErrorBody, ErrorCode } from './errors'

/**
 * Client HTTP minimal de l'API `/api/v1`, partagé par le web (cookie de session) et le mobile
 * (jeton `Authorization: Bearer`, fourni par `getAccessToken`).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network_error',
    readonly details?: Record<string, unknown>,
  ) {
    super(code)
    this.name = 'ApiError'
  }
}

export interface ApiClientOptions {
  baseUrl?: string
  getAccessToken?: () => string | null | undefined
  fetchImpl?: typeof fetch
}

export function createApiClient(options: ApiClientOptions = {}) {
  const baseUrl = (options.baseUrl ?? '').replace(/\/$/, '') + '/api/v1'
  const fetchImpl = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args))

  async function request<T>(method: string, path: string, body?: unknown, asForm = false): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' }
    if (body !== undefined && !asForm) headers['content-type'] = 'application/json'
    const token = options.getAccessToken?.()
    if (token) headers.authorization = `Bearer ${token}`
    let res: Response
    try {
      res = await fetchImpl(baseUrl + path, {
        method,
        headers,
        credentials: 'include',
        body: body === undefined ? undefined : asForm ? (body as FormData) : JSON.stringify(body),
      })
    } catch {
      throw new ApiError(0, 'network_error')
    }
    if (res.status === 204) return undefined as T
    const data = (await res.json().catch(() => null)) as unknown
    if (!res.ok) {
      const err = (data as ApiErrorBody | null)?.error
      throw new ApiError(res.status, err?.code ?? 'internal_error', err?.details)
    }
    return data as T
  }

  return {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
    /** Envoi multipart (ne pas fixer Content-Type : le navigateur ajoute la boundary). */
    postForm: <T>(path: string, body: FormData) => request<T>('POST', path, body, true),
    patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
    put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
    delete: <T>(path: string) => request<T>('DELETE', path),
  }
}

export type ApiClient = ReturnType<typeof createApiClient>
